import { supabase } from '@/integrations/supabase/client';

// MailerLite Group IDs
export const MAILERLITE_GROUPS = {
  ONBOARDING: '170939451646673985',
  REACTIVATION: '170939451697005676',
  ACCOUNT_DELETION: '170939451965441296',
} as const;

export type EmailFlow = 'onboarding' | 'reactivation' | 'account_deletion';

/**
 * Enroll a user in a MailerLite email flow
 */
export async function enrollUserInEmailFlow(
  userId: string,
  email: string,
  displayName: string | null,
  flow: EmailFlow
): Promise<{ success: boolean; message: string }> {
  const { data: { session } } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('No active session');
  }

  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const response = await fetch(`${supabaseUrl}/functions/v1/admin-metrics?action=enroll_email_flow`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
      'apikey': supabaseAnonKey,
    },
    body: JSON.stringify({
      userId,
      email,
      displayName,
      flow,
      groupId: MAILERLITE_GROUPS[flow.toUpperCase() as keyof typeof MAILERLITE_GROUPS],
    }),
  });

  if (!response.ok) {
    const errorData = await response.json();
    throw new Error(errorData.error || 'Failed to enroll user in email flow');
  }

  const result = await response.json();
  return result;
}

/**
 * Get a user's enrolled email flows
 */
export async function getUserEmailFlows(userId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('profiles')
    .select('email_flows')
    .eq('user_id', userId)
    .single();

  if (error) {
    console.error('Error fetching user email flows:', error);
    return [];
  }

  return data?.email_flows || [];
}

/**
 * Check if user is enrolled in a specific flow
 */
export async function isUserEnrolledInFlow(
  userId: string,
  flow: EmailFlow
): Promise<boolean> {
  const flows = await getUserEmailFlows(userId);
  return flows.includes(flow);
}

/**
 * Enroll a new user in the onboarding email flow
 * This is called automatically when a user signs up
 */
export async function enrollNewUserInOnboarding(
  userId: string,
  email: string,
  displayName: string
): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      console.error('[MailerLite] No active session for onboarding enrollment');
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/enroll-onboarding-flow`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          userId,
          email,
          displayName,
        }),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error('[MailerLite] Failed to enroll in onboarding:', errorText);
      return;
    }

    await response.json();
  } catch (error) {
    console.error('[MailerLite] Error enrolling in onboarding flow:', error);
    // Don't throw - we don't want to block the auth flow
  }
}

/**
 * Remove the signed-in user from the account deletion MailerLite group
 * (best-effort, non-blocking).
 */
async function unenrollFromEmailFlow(flow: 'account_deletion' | 'reactivation'): Promise<void> {
  try {
    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      console.error(`[MailerLite] No active session for ${flow} unenroll`);
      return;
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const supabaseAnonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    const response = await fetch(
      `${supabaseUrl}/functions/v1/unenroll-email-flow?flow=${flow}`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': supabaseAnonKey,
        },
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[MailerLite] Failed to unenroll from ${flow}:`, errorText);
      return;
    }

    await response.json().catch(() => null);
  } catch (error) {
    console.error(`[MailerLite] Error unenrolling from ${flow} flow:`, error);
  }
}

/**
 * Remove the signed-in user from the account deletion MailerLite group
 * (best-effort, non-blocking).
 */
export async function unenrollFromAccountDeletionFlow(): Promise<void> {
  return unenrollFromEmailFlow('account_deletion');
}

/**
 * Remove the signed-in user from the reactivation MailerLite group
 * (best-effort, non-blocking).
 */
export async function unenrollFromReactivationFlow(): Promise<void> {
  return unenrollFromEmailFlow('reactivation');
}
